import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import StyledEntityFinanceHistoryProfileCardContent
  from 'components/organisms/Cards/EntityFinanceHistoryProfileCardContent/EntityFinanceHistoryProfileCardContent.styles';
import Tabs from 'components/atoms/Tabs/Tabs/Tabs';
import Tab from 'components/atoms/Tabs/Tab/Tab';
import TabPanel from 'components/atoms/Tabs/TabPanel/TabPanel';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import constants from 'helpers/constants';
import Box from 'components/atoms/Layout/Box/Box';
import ActionIconButton from 'components/molecules/Buttons/ActionIconButton/ActionIconButton';
import Typography, {P} from 'components/atoms/Text/Typography/Typography';
import ConfirmDialog from 'components/organisms/Dialogs/ConfirmDialog/ConfirmDialog';
import {useDialogControl} from 'components/organisms/Providers/DialogProvider/DialogProvider';
import Delete from '@mui/icons-material/Delete';
import ActionButton from 'components/molecules/Buttons/ActionButton/ActionButton';
import {useSnackbar} from 'components/organisms/Providers/SnackbarProvider/SnackbarProvider';
import Add from '@mui/icons-material/Add';
import Icon from 'components/atoms/Icons/Icon/Icon';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Link from 'components/atoms/Links/Link/Link';
import Search from '@mui/icons-material/Search';
import {useProfile} from 'components/organisms/Providers/ProfileProvider/ProfileProvider';
import {processEntity, processEntityFeedback, processEntityInfo} from 'services/entity/entity.utils';
import CircularProgress from 'components/atoms/Progress/CircularProgress/CircularProgress';
import EntityEnrichDialog from 'components/organisms/Dialogs/EntityEnrichDialog/EntityEnrichDialog';
import Markdown from 'components/atoms/Formatters/Markdown/Markdown';

const EntityFinanceHistoryProfileCardContent = React.forwardRef((props, ref) => {
  const {
    profile,
    card,
    content,
    entity,
    collection,
    isLoading,
    fieldData,
    isDialog,
    onValidating,
    onSubmit,
    onPatch,
    ...innerProps
  } = useComponentProps(props, 'EntityFinanceHistoryProfileCardContent', {
    static: ['isEditing', 'hasHistory']
  });

  const formRef = useRef(null);
  const innerRef = useRef(null);
  const tabsRef = useRef(null);
  const [internalState, setInternalState] = useState({});
  const [activeTab, setActiveTab] = useState(null);

  const activeHistory = internalState.history?.[activeTab];

  const profileProvider = useProfile();
  const enrichEvent = useEffectEvent(profileProvider.updaters?.enrichData);
  const setSettingsEvent = useEffectEvent(profileProvider.setSettings);

  const dialogControl = useDialogControl();
  const snackbar = useSnackbar();

  // ProfileCardContent handles form state
  // fake form when empty triggers validating and is submittable
  useImperativeHandle(ref, () => formRef.current);

  const isEditing = content.state.isEditing;

  const history = useMemo(() => {
    return (entity?.financeHistory ?? [])
      .map((h) => utils.camelcase(h))
      .map((h) => ({...h, initial: h}))
      .sort((a, b) => +a.year - +b.year);
  }, [entity?.financeHistory]);

  useEffect(() => {
    setInternalState({
      initial: utils.clone(history, true),
      history: utils.clone(history, true)
    });

    setActiveTab(history.length > 0 ? (history.length - 1) : 0);

    return utils.observeRetry(() => {
      if (tabsRef.current) {
        const el = tabsRef.current.querySelector('.MuiTabs-scroller');
        if (el) {
          el.scrollTo({left: el.scrollWidth - el.clientWidth});
        }
      }
    }, 8);
  }, [history, isEditing]);

  const fields = useMemo(() => {
    const fields = [];

    if (activeHistory) {
      if (isEditing) {
        fields.push({
          name: 'year',
          label: 'Year',
          entity: 'entity',
          type: constants.formFieldTypes.autocomplete,
          validation: constants.formFieldValidationTypes.year,
          conversion: constants.formFieldConversionTypes.value,
          validate: (value, testContext) => {
            const duplicate = +value !== +activeHistory?.year && internalState.history
              .find((h, idx) => idx !== activeTab && +h.year === +value);

            if (duplicate) {
              return testContext.createError({message: `Enter a unique year, ${value} already exists`});
            } else {
              return true;
            }
          },
          initial: activeHistory.initial?.year,
          options: 'years',
          required: true,
          readOnly: !isEditing,
          debounce: content.state.canPatch ? constants.debounce.input : false,
          FormFieldProps: {
            autoFocus: false,
            hiddenPlaceholder: !isEditing,
            variant: isEditing ? 'staticLabel' : 'inlineLabel'
          }
        });
      }

      constants.data.financeFields
        .filter((f) => isEditing || utils.isDefined(activeHistory[f.value]))
        .forEach((f) => {
          fields.push({
            name: f.value,
            label: f.label,
            entity: 'entity',
            renderer: constants.fieldRenderers.monetary,
            type: constants.formFieldTypes.monetary,
            validation: f.validation ?? constants.formFieldValidationTypes.int,
            conversion: constants.formFieldConversionTypes.value,
            format: constants.formFieldFormatTypes.int,
            prefix: fieldData?.currency?.symbol,
            initial: activeHistory.initial?.[f.value] ?? '',
            readOnly: !isEditing,
            options: 'currencyConversions',
            description: '',
            debounce: content.state.canPatch ? constants.debounce.input : false,
            FormFieldProps: {
              autoFocus: false,
              hiddenPlaceholder: !isEditing,
              variant: isEditing ? 'staticLabel' : 'inlineLabel'
            }
          });
        });
    }

    return fields;
  }, [content.state.canPatch, internalState.history, fieldData?.currency?.symbol, activeTab, activeHistory, isEditing]);

  const onSubmitEvent = useEffectEvent(onSubmit);
  const enrichingEntity = Boolean(profileProvider.state?.settings?.enrichingEntities?.[+entity?.entityId]);
  const enrichingFinance = Boolean(profileProvider.state?.settings?.enrichingFinance?.[+entity?.entityId]);
  const enrichAction = useMemo(() => {
    const hasHistory = internalState.history?.length > 0;
    const companyInfoEnrichment = constants.data.lookup('services', constants.services.types.companyInfoEnrichment);
    const creditSafeEnrichment = constants.data.lookup('services', constants.services.types.creditSafeEnrichment);

    const getEnrichmentType = (country) => {
      if (creditSafeEnrichment.enabled && creditSafeEnrichment.countries.find((cc) => cc.toLowerCase() === country?.toLowerCase())) {
        return 'creditSafe';
      } else if (companyInfoEnrichment.enabled && companyInfoEnrichment.countries.find((cc) => cc.toLowerCase() === country?.toLowerCase())) {
        return 'companyInfo';
      } else {
        return null;
      }
    };

    const enrichmentType = getEnrichmentType(entity?.location?.country);
    const enrichmentStats = entity?.enrichmentStats?.[enrichmentType];
    const enrichTime = enrichmentStats?.updatedAt ? new Date(enrichmentStats?.updatedAt) : null;

    const disabledReason = (enrichmentType || !entity?.location?.country) ? null :
      `No valid enrichment service found for ${constants.data.lookup('countries', entity?.location?.country?.toUpperCase())?.label}`;

    return {
      label: !hasHistory ? 'Get financials' : 'Update financials',
      tooltip: disabledReason ? disabledReason : (
        enrichTime ? `Last update ${utils.timeAgo(enrichTime)}` : null
      ),
      icon: !enrichingFinance ? Search : <CircularProgress/>,
      auth: utils.createAuth({attribute: 'entity.enrichFinance'}),
      onClick: (e) => {
        const setEnriching = (active) => {
          setSettingsEvent?.((current) => ({
            ...current,
            enrichingEntities: {
              ...current?.enrichingEntities,
              [+entity?.entityId]: active
            },
            enrichingFinance: {
              ...current?.enrichingFinance,
              [+entity?.entityId]: active
            }
          }));
        }

        const doEnrichment = (entity, enrichmentType) => {
          const enrichingSnackbar = snackbar.show(`${!hasHistory ? 'Getting' : 'Updating'} financials...`, null, {
            color: 'info',
            autoHideDuration: null
          });

          enrichEvent?.({
            entityId: entity?.entityId,
            collectionId: collection?.collectionId,
            enrichmentTypes: [enrichmentType],
            useAvailableBudget: false
          })
            .then((res) => {
              const entity = processEntity(res.response.data.data);
              const history = (entity?.financeHistory ?? [])
                .map((h) => utils.camelcase(h))
                .map((h) => ({...h, initial: h}))
                .sort((a, b) => +a.year - +b.year);

              if (enrichmentType === 'companyInfo') {
                if (Boolean(entity?.enrichmentStats?.companyInfo?.cocNumberFeedback)) {
                  snackbar.show('Verify the company CoC number for more data', null, {
                    color: 'warning',
                    autoHideDuration: constants.delay.long
                  });
                } else if (!entity?.cocNumber) {
                  snackbar.show('Fill in the company CoC number for more data', null, {
                    color: 'warning',
                    autoHideDuration: constants.delay.long
                  });
                } else if (utils.compare(internalState.history, history)) {
                  snackbar.show('No new financial data found', null,
                    {color: 'warning', autoHideDuration: constants.delay.warning});
                }
              } else if (enrichmentType === 'creditSafe') {
                if (Boolean(entity?.enrichmentStats?.creditSafe?.creditSafeIdFeedback)) {
                  snackbar.show('Verify the company Creditsafe ID for more data', null, {
                    color: 'warning',
                    autoHideDuration: constants.delay.long
                  });
                } else if (!entity?.creditSafeId) {
                  snackbar.show('Fill in the company Creditsafe ID for more data', null, {
                    color: 'warning',
                    autoHideDuration: constants.delay.long
                  });
                } else if (utils.compare(internalState.history, history)) {
                  snackbar.show('No new financial data found', null,
                    {color: 'warning', autoHideDuration: constants.delay.warning});
                }
              }
            })
            .catch((error) => {
              if (error?.response?.status === constants.http.status.conflict) {
                snackbar.show(`Not enough credits to ${!hasHistory ? 'get' : 'update'} financials`, null,
                  {color: 'warning', autoHideDuration: constants.delay.warning});
              } else {
                snackbar.show(`${!hasHistory ? 'Getting' : 'Updating'} financials failed`, null,
                  {color: 'error', autoHideDuration: constants.delay.error});
              }
            })
            .finally(() => {
              setEnriching(false);
              snackbar.hide(enrichingSnackbar);
            });
        }

        setEnriching(true);

        let fields = [];
        const cocNumberFeedback = processEntityFeedback(entity, {name: 'cocNumber'}, entity?.cocNumber);
        const creditSafeIdFeedback = processEntityFeedback(entity, {name: 'creditSafeId'}, entity?.creditSafeId);

        const addField = (name, relation, required) => {
          profile.cards.some((c) => {
            return c.contents?.some((c) => {
              return c.fields?.some((field) => {
                if (utils.camelcase(field.name) === name) {
                  fields.push({
                    ...field,
                    required: required,
                    relation: relation
                  });
                  return true;
                } else {
                  return false;
                }
              })
            });
          });
        };

        if (!entity?.location?.country) {
          addField('location', null, true);
        }
        if (enrichmentType === 'companyInfo' || !entity?.location?.country) {
          if (!entity?.cocNumber || cocNumberFeedback?.show) {
            const countries = companyInfoEnrichment.countries
              .filter((cc1) => !creditSafeEnrichment.countries.find((cc2) => cc1.toLowerCase() === cc2.toLowerCase()));

            if (countries.length > 0) {
              addField('cocNumber', !entity?.location?.country ? `location(${countries.join(',')})` : null, false);
            }
          }
        }
        if (enrichmentType === 'creditSafe' || !entity?.location?.country) {
          if (!entity?.creditSafeId || creditSafeIdFeedback?.show) {
            const countries = creditSafeEnrichment.countries;

            if (countries.length > 0) {
              addField('creditSafeId', !entity?.location?.country ? `location(${creditSafeEnrichment.countries.join(',')})` : null, false);
            }
          }
        }

        if (fields.length > 0) {
          let startedEnriching = false;

          const handleClose = () => {
            if (!startedEnriching) {
              setEnriching(false);
              dialogControl.hide();
            }
          };

          const handleSubmit = (values, fields) => {
            return utils.asPromiseCallback(onSubmitEvent)(values, null, fields, entity, true)
              .then(() => {
                const country = values?.location?.country ?? entity?.location?.country;
                const enrichmentType = getEnrichmentType(country);
                if (!enrichmentType) {
                  snackbar.show(`No valid enrichment service found for ${constants.data.lookup('countries', country?.toUpperCase())?.label}`, null,
                    {color: 'warning', autoHideDuration: constants.delay.warning});
                } else {
                  dialogControl.hide();
                  startedEnriching = true;
                  doEnrichment(entity, enrichmentType);
                }
              });
          }

          dialogControl.show(<EntityEnrichDialog entity={entity}
                                                 fields={fields}
                                                 fieldData={fieldData}
                                                 onSubmit={handleSubmit}/>, true, handleClose);
        } else {
          doEnrichment(entity, enrichmentType);
        }

        e.preventDefault();
      },
      TooltipProps: disabledReason ? {
        enterDelay: 0
      } : null,
      ButtonProps: {
        disabled: disabledReason || enrichingEntity || enrichingFinance
      }
    }
  }, [internalState.history, entity, enrichingEntity, enrichingFinance, snackbar, enrichEvent,
    collection?.collectionId, profile.cards, dialogControl, fieldData, setSettingsEvent, onSubmitEvent]);

  const cleanHistory = (history) => {
    if (history) {
      return history.map((h) => {
        return utils.underscoreEx(
          utils.cleanObject(
            utils.filterObject(h, ['initial']),
            false
          )
        );
      })
        .sort((a, b) => +b.year - +a.year);
    } else {
      return history;
    }
  }

  const handleSubmit = useCallback((values, actions) => {
    const name = 'finance_history';
    const fields = [{
      name: 'finance_history'
    }];

    let newHistory = utils.clone(internalState.history, true);
    if (newHistory[activeTab]) {
      newHistory[activeTab] = {...newHistory[activeTab], ...values};
      newHistory.sort((a, b) => +a.year - +b.year);
      setInternalState(utils.updater({history: newHistory}, true));
    } else {
      newHistory = null;
    }

    const changes = {
      [name]: cleanHistory(newHistory),
    };

    onSubmitEvent?.(changes, actions, fields);
  }, [activeTab, internalState.history, onSubmitEvent]);

  const canPatch = Boolean(content.state.isEditing && !isDialog && !profile.state.isEditing);
  const onPatchEvent = useEffectEvent(onPatch);
  const handleChange = useCallback((field, value, onSuccess, onError) => {
    const historyField = {
      name: 'finance_history'
    };

    const currentTab = activeTab;
    const oldHistory = internalState.history;
    const newHistory = utils.clone(internalState.history, true);
    newHistory[activeTab][field.name] = value;
    newHistory[activeTab].initial[field.name] = value;

    if (field.name === 'year') {
      newHistory.sort((a, b) => +a.year - +b.year);
      setActiveTab(newHistory.findIndex((h) => +h.year === +value));
    }
    setInternalState(utils.updater({history: newHistory}, true));

    if (canPatch) {
      return onPatchEvent?.(historyField, cleanHistory(newHistory), onSuccess, (err) => {
        setInternalState(utils.updater({history: oldHistory}, true));
        setActiveTab(currentTab);
        onError?.(err);
      });
    }
  }, [activeTab, canPatch, internalState.history, onPatchEvent]);

  const handleAddClick = () => {
    const historyField = {
      name: 'finance_history'
    };

    let openYear = (internalState.history ?? []).reverse().reduce((y, h) => +h.year === +y ? y - 1 : y, (new Date()).getFullYear() - 1);
    if (openYear === 0) {
      openYear = (internalState.history ?? []).reverse().reduce((y, h) => +h.year === +y ? y + 1 : y, (new Date()).getFullYear() - 1);
    }

    const oldHistory = internalState.history;
    const newHistory = [{
      year: openYear,
      initial: {
        year: openYear
      }
    }]
      .concat(internalState.history ?? [])
      .sort((a, b) => +a.year - +b.year);

    const currentTab = activeTab;
    setActiveTab(newHistory.findIndex((h) => +h.year === openYear));
    setInternalState(utils.updater({history: newHistory}, true));

    if (canPatch) {
      return onPatch?.(historyField, cleanHistory(newHistory), null, () => {
        setActiveTab(currentTab);
        setInternalState(utils.updater({history: oldHistory}, true));
        snackbar.show('Adding year failed', null,
          {color: 'error', autoHideDuration: constants.delay.error});
      });
    }
  }

  const handleRemoveClick = (e) => {
    const handleConfirm = () => {
      const historyField = {
        name: 'finance_history'
      };
      const oldHistory = internalState.history;
      const newHistory = (internalState.history ?? []).filter((h, idx) => idx !== activeTab);

      const currentTab = activeTab;
      setActiveTab((current) => {
        return newHistory.length > current ? current : ((current === 0) ? null : current - 1);
      });
      setInternalState(utils.updater({history: newHistory}, true));

      if (canPatch) {
        return utils.asPromiseCallback(onPatch)(historyField, cleanHistory(newHistory))
          .catch(() => {
            setActiveTab(currentTab);
            setInternalState(utils.updater({history: oldHistory}, true));
            throw new Error('Removing year failed');
          });
      } else {
        return Promise.resolve();
      }
    };

    if (canPatch) {
      dialogControl.show(<ConfirmDialog question="Are you sure you want to remove this year?"
                                        explanation="The year wil be removed from this company's history"
                                        onConfirm={handleConfirm}
                                        ConfirmButtonProps={{
                                          children: 'Remove year',
                                          startIcon: <Icon icon={Delete} />,
                                          color: 'error'
                                        }}/>, true);
    } else {
      handleConfirm();
    }

    e.preventDefault();
  }

  const handleTabChange = (e, value) => {
    setActiveTab(value);
  }

  const onValidatingEvent = useEffectEvent(onValidating);
  const handleValidating = useCallback((validating, dirty, errors) => {
    const isDirty = !utils.compare(internalState.initial, internalState.history) || dirty;

    onValidatingEvent?.(validating, isDirty, errors);
  }, [internalState.history, internalState.initial, onValidatingEvent]);

  const renderHistoryForm = () => {
    return <React.Fragment>
      <InlineForm ref={formRef}
                  className="EntityFinanceHistoryProfileCardContent-form"
                  fields={fields}
                  fieldData={fieldData}
                  onChange={handleChange}
                  onSubmit={handleSubmit}
                  onValidating={handleValidating}/>
      {!(fields?.length > 0) ? <Box className="EntityFinanceHistoryProfileCardContent-empty">
        <P>No financial data</P>
      </Box> : null}
    </React.Fragment>
  }

  const renderYearSelector = () => {
    if (internalState.history?.length > 0 || isEditing) {
      return <Box className="EntityFinanceHistoryProfileCardContent-years">
        <Typography disabled={content.state.isSubmitting} variant="subtitle1">History</Typography>
        {internalState.history?.length > 0 ? <Tabs ref={tabsRef}
                                                   variant="scrollable"
                                                   density="dense"
                                                   size="small"
                                                   value={activeTab ?? 0}
                                                   onChange={handleTabChange}
                                                   disabled={content.state.isSubmitting}
                                                   className="EntityFinanceHistoryProfileCardContent-tabs">
          {internalState.history?.map((h, idx) => {
            return <Tab key={idx} value={idx} label={h.year}/>
          })}
        </Tabs> : null}
        {isEditing ? <ActionIconButton variant="outlined"
                                       density="denser"
                                       IconProps={{
                                         size: 'tiny'
                                       }}
                                       action={{
                                         icon: Add,
                                         tooltip: 'Add year',
                                         onClick: handleAddClick,
                                         IconButtonProps: {
                                           disabled: content.state.isSubmitting
                                         }
                                       }}/> : null}
      </Box>
    }
  }

  const renderFooter = () => {
    if (!isEditing) {
      const source = entity?.availableSources?.used?.['finance_history'];
      const fieldInfo = processEntityInfo(entity, {name: 'finance_history'});
      const diskSource = constants.data.lookup('diskSourceTypes', source);

      const showInfo = activeHistory && (fieldInfo || diskSource?.link);

      return <Box className="EntityFinanceHistoryProfileCardContent-footer">
        {showInfo ?
          <Box className="EntityFinanceHistoryProfileCardContent-info">
            <Icon icon={InfoOutlined} color="primary" size="smaller"/>
            {fieldInfo ?
              <Typography variant="caption" color="text.secondary" showTooltip={true} TooltipProps={{enterDelay: 0}}>
                <Markdown>{fieldInfo}</Markdown>
              </Typography> :
              <Typography variant="caption" color="text.secondary" showTooltip={true} TooltipProps={{enterDelay: 0}}>
                Finance history is based on <Link href={utils.cleanExternalLink(diskSource.link)}
                                                  target="_blank">{diskSource.label}</Link> data
                {activeHistory.info ?
                  <React.Fragment>&nbsp;for {activeHistory.info.name}{activeHistory.info.consolidated ? ' (consolidated)' : ''}</React.Fragment> : null}
              </Typography>
            }
          </Box> : null}
        <Box className="EntityFinanceHistoryProfileCardContent-buttons">
          <ActionButton className="EntityFinanceHistoryProfileCardContent-enrichAction"
                        action={enrichAction}
                        variant="text"
                        isLoading={isLoading}/>
        </Box>
      </Box>
    }
  }

  innerProps.className = utils.flattenClassName(innerProps.className, {
    isEditing: isEditing
  });

  return <StyledEntityFinanceHistoryProfileCardContent ref={innerRef} {...innerProps}>
    {renderYearSelector()}
    {internalState.history?.map((h, idx) => {
      return <TabPanel key={h.year}
                       className="EntityFinanceHistoryProfileCardContent-tab"
                       value={activeTab ?? 0}
                       index={idx}>
        {renderHistoryForm()}
        {isEditing ? <ActionButton color="error"
                                   variant="contained"
                                   action={{
                                     label: 'Remove year',
                                     icon: Delete,
                                     onClick: handleRemoveClick,
                                     ButtonProps: {
                                       disabled: content.state.isSubmitting
                                     }
                                   }} /> : null}
      </TabPanel>
    })}
    {!(internalState.history?.length > 0) ? <Box className="EntityFinanceHistoryProfileCardContent-empty">
      <P>No financial data</P>
      <InlineForm ref={formRef}
                  onSubmit={handleSubmit}
                  onValidating={handleValidating}/>
    </Box> : null}
    {renderFooter()}
  </StyledEntityFinanceHistoryProfileCardContent>
});

EntityFinanceHistoryProfileCardContent.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  profile: PropTypes.object,
  card: PropTypes.object,
  content: PropTypes.object,
  entity: PropTypes.object,
  collection: PropTypes.object,
  isLoading: PropTypes.bool,
  fieldData: PropTypes.object,
  onValidating: PropTypes.func,
  onSubmit: PropTypes.func,
  onPatch: PropTypes.func
};

export default EntityFinanceHistoryProfileCardContent;


